import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { useCachedAccess } from '@/lib/access/use-access';

// The operator's header airport scope (NEX-310 / operator-context S5).
//
// URL-driven per frontend-conventions §4.1: the selection lives in `?airports=`
// — a CSV of CONCRETE IATA-3 codes, the NEX-308 frozen wire — so a filtered
// view is shareable and survives reload. Query params don't travel across
// route navigations, so a localStorage fallback carries the scope onto pages
// whose URL doesn't name it (the same URL-wins / stored-default split the
// disruptions board's scope lens uses). "All airports" = empty set = the param
// absent AND the stored value cleared.
//
// BOUNDED by the operator's access (users-roles-permissions R5, brief §4.18): the
// selection can never leave the operator's effective airports (`GET /v1/me › access
// .airports`). A URL or stored value naming other airports is intersected away, and an
// empty selection means "all of MINE" — sent as the explicit list, because on the wire an
// absent `airports=` means "no narrowing" and M1's BFF does not narrow lists itself yet.
// Unbounded operators (`'*'`) and an access that is not known yet keep today's behaviour.

const PARAM = 'airports';
const STORAGE_KEY = 'nexa.airport-scope';

/** Normalize a set of raw codes: upper, IATA-3 shaped only, deduped, sorted. */
export function normalizeAirports(codes: Iterable<string>): string[] {
  const out = new Set<string>();
  for (const raw of codes) {
    const code = String(raw).trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(code)) out.add(code);
  }
  return [...out].sort();
}

/** Parse the `?airports=` param value. Absent (null) is the caller's cue to
 *  fall back to the stored scope; present-but-empty means explicitly cleared. */
export function parseAirportsParam(raw: string | null): string[] {
  if (!raw) return [];
  return normalizeAirports(raw.split(','));
}

/** The CSV the list APIs put on the wire, or undefined for "no narrowing". */
export function airportsCsv(airports: string[]): string | undefined {
  return airports.length > 0 ? airports.join(',') : undefined;
}

/** Compact picker-trigger label: All airports · GRU · GRU +2. */
export function scopeSummary(airports: string[], allLabel: string): string {
  if (airports.length === 0) return allLabel;
  const [first] = airports;
  return airports.length === 1 ? first! : `${first} +${airports.length - 1}`;
}

function readStored(): string[] {
  try {
    return parseAirportsParam(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

function writeStored(airports: string[]): void {
  try {
    if (airports.length > 0) window.localStorage.setItem(STORAGE_KEY, airports.join(','));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable (private mode) — the URL still carries the scope.
  }
}

/** An operator's airport bound: the whole airline, a list, or unknown (`null`). */
export type AirportBound = readonly string[] | '*' | null;

/**
 * The selection an operator may actually use: `selected` ∩ `bound`, or the whole bound
 * when that leaves nothing ("all of mine"). Unbounded / unknown → `selected` unchanged.
 */
export function boundSelection(selected: string[], bound: AirportBound): string[] {
  if (bound === null || bound === '*') return selected;
  const mine = new Set(bound);
  const kept = selected.filter((code) => mine.has(code));
  return kept.length > 0 ? kept : [...bound];
}

/** Does the selection cover exactly the operator's whole bound ("Mis aeropuertos")? */
export function isAllOfBound(airports: string[], bound: AirportBound): boolean {
  if (bound === null || bound === '*') return airports.length === 0;
  return bound.length === airports.length && bound.every((code) => airports.includes(code));
}

/**
 * The header airport scope: `airports` is the selected IATA set ([] = all, for an
 * unbounded operator), `setAirports` replaces it (empty clears). URL wins; the stored
 * value covers a navigation whose URL doesn't carry the param. `bound` is the operator's
 * airport bound (see above).
 */
export function useAirportScope(): {
  airports: string[];
  setAirports: (codes: string[]) => void;
  bound: AirportBound;
} {
  const [params, setParams] = useSearchParams();
  // Passive: the access boundary loads it; list pages must not each fetch `/v1/me`.
  const access = useCachedAccess();
  const bound: AirportBound = access.ready ? access.airports : null;
  const fromUrl = params.get(PARAM);
  const selected = fromUrl !== null ? parseAirportsParam(fromUrl) : readStored();
  const airports = boundSelection(selected, bound);

  const setAirports = useCallback(
    (codes: string[]) => {
      const cleaned = normalizeAirports(codes);
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (cleaned.length > 0) next.set(PARAM, cleaned.join(','));
          else next.delete(PARAM);
          return next;
        },
        { replace: true },
      );
      writeStored(cleaned);
    },
    [setParams],
  );

  return { airports, setAirports, bound };
}
